import {
  authCheck,
  authRequestAuthCode,
  authRestore,
  authSignIn,
  authSignOut,
  authSignUp,
  clearActiveTournamentId,
  switchUserTournament,
} from './actions';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { AuthenticatedUser } from '../domain/interfaces/auth-sign-in-response';
import { loadState } from './persist';

type AuthSliceState = {
  loading: boolean;
  error: { [key: string]: string } | null;
  authed: boolean;
  authChecking: boolean;
  authChecked: boolean;
  user: AuthenticatedUser | null;
  activeTournamentId: number | null;
  hasTournaments: boolean;
  codeRequesting: boolean;
  codeRequested: boolean;
  signInError: string | null;
};

const persistedState = loadState();

const initialState: AuthSliceState = {
  loading: false,
  error: null,
  authed: false,
  authChecking: false,
  authChecked: false,
  user: null,
  hasTournaments: false,
  codeRequesting: false,
  codeRequested: false,
  signInError: null,
  ...persistedState,
};

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetAuthChecking(state) {
      state.codeRequested = false;
      state.codeRequesting = false;
    },
    setTournamentCreated(state, action: PayloadAction<number>) {
      state.hasTournaments = true;
      if (!state.activeTournamentId) {
        state.activeTournamentId = action.payload;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(authRestore.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(authRestore.fulfilled, (state) => {
      state.loading = false;
    });

    builder.addCase(authRestore.rejected, (state) => {
      state.loading = false;
    });

    builder.addCase(authRequestAuthCode.pending, (state) => {
      state.codeRequesting = true;
    });

    builder.addCase(authRequestAuthCode.fulfilled, (state) => {
      state.codeRequesting = false;
      state.codeRequested = true;
    });

    builder.addCase(authRequestAuthCode.rejected, (state) => {
      state.codeRequesting = false;
      state.codeRequested = false;
    });

    builder.addCase(authSignIn.pending, (state) => {
      state.loading = true;
      state.signInError = null;
    });

    builder.addCase(authSignIn.fulfilled, (state) => {
      state.loading = false;
      state.authed = true;
      state.codeRequested = false;
      state.signInError = null;
    });

    builder.addCase(authSignIn.rejected, (state, action) => {
      state.loading = false;
      state.authed = false;
      state.signInError = action.error.message ?? null;
    });

    builder.addCase(authSignUp.pending, (state) => {
      state.codeRequesting = true;
    });

    builder.addCase(authSignUp.fulfilled, (state, action) => {
      state.codeRequesting = false;
      state.codeRequested = true;

      if (action.payload.errors) {
        state.error = Object.keys(action.payload.errors!).reduce<{ [key: string]: string }>((result, key) => {
          result[key] = action.payload.errors![key][0];
          return result;
        }, {});
      }
    });

    builder.addCase(authSignUp.rejected, (state) => {
      state.codeRequesting = false;
      state.codeRequested = false;
    });

    builder.addCase(authCheck.pending, (state) => {
      state.authChecking = true;
    });

    builder.addCase(authCheck.fulfilled, (state, action) => {
      const user = action.payload?.user || null;
      state.hasTournaments = Boolean(action.payload?.tournament) || false;

      if (!action.payload?.tournament) {
        state.activeTournamentId = null;
      } else if (!state.activeTournamentId) {
        state.activeTournamentId = action.payload.tournament.id;
      }

      state.authed = true;
      state.authChecking = false;
      state.authChecked = true;
      state.user = user;
    });

    builder.addCase(authCheck.rejected, (state) => {
      state.authed = false;
      state.authChecking = false;
      state.authChecked = true;
      state.user = null;
    });

    builder.addCase(authSignOut, (state) => {
      state.authed = false;
      state.user = null;
      state.hasTournaments = false;
      state.activeTournamentId = null;
    });

    builder.addCase(switchUserTournament, (state, action) => {
      state.activeTournamentId = action.payload;
    });

    builder.addCase(clearActiveTournamentId, (state) => {
      state.activeTournamentId = null;
    });

  },
});

export const { resetAuthChecking, setTournamentCreated } = slice.actions;

const authReducer = slice.reducer;
export default authReducer;
